<template>
  <BaseModal ref="modal" modal-classes="max-w-md mx-auto" @modal-after-leave="$emit('closed')">
    <template v-if="!data.id" #header>
      <span class="text-xs text-gray-700 uppercase tracking-wider">Δημιουργία</span>
      <h6 class="heading">Ad Plan</h6>
    </template>

    <template v-else #header>
      <span class="text-xs text-gray-700 uppercase tracking-wider">Τροποποιηση</span>
      <h6 class="heading">Ad Plan</h6>
    </template>

    <template #default="modalProps">
      <ValidationObserver slim v-slot="{ handleSubmit, invalid }" ref="observer">
        <form @submit.prevent="handleSubmit(doSubmit)">
          <BaseLayoutSpaced relaxed>
            <BaseLayoutSpaced>
              <ValidationProvider
                v-for="field in formFieldsWithDefaults"
                :key="field.name"
                :name="field.name"
                :vid="field.name"
                :rules="field.validation"
                v-slot="{ errors, failed }"
                slim
              >
                <BaseFormGroup>
                  <BaseFormLabel :for="`${_uid}-${field.name}`">
                    {{ field.label }} <template v-if="field.validation.required"> * </template>
                  </BaseFormLabel>
                  <Component
                    v-model="data[field.name]"
                    :is="field.component"
                    :disabled="submitting"
                    v-bind="{
                      ...field.options.props,
                      ...field.options.attrs,
                    }"
                    :invalid="failed"
                    :id="`${_uid}-${field.name}`"
                  />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </ValidationProvider>
            </BaseLayoutSpaced>
            <div class="flex space-x-2 py-1 items-baseline">
              <BaseButton primary type="submit" :disabled="submitting || invalid">
                <span v-if="!submitting">Submit</span>
                <Loader v-else small inverse />
              </BaseButton>
              <BaseButton ghost type="button" @click="modalProps.close" :disabled="submitting">
                Close
              </BaseButton>
              <BaseFormInlineMessage v-if="message" danger>
                {{ message }}
              </BaseFormInlineMessage>
            </div>
          </BaseLayoutSpaced>
        </form>
      </ValidationObserver>
    </template>
  </BaseModal>
</template>

<script>
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

const defaultField = {
  component: null,
  label: '',
  name: '',
  options: {},
  validation: {},
};

const defaultData = {
  name: null,
  description: null,
  dateStart: null,
  dateEnd: null,
  target: null,
};

export default {
  name: 'ModalEdit',
  mixins: [veeValidateErrorsMixin],
  props: {
    campaign: {
      type: Object,
      default: () => ({}),
    },
    action: {
      type: Function,
      required: true,
    },
    closeOnSuccess: Boolean,
  },
  data: () => ({
    submitting: false,
    data: null,
    message: null,
  }),
  computed: {
    formFields() {
      return [
        {
          component: 'BaseFormInput',
          label: 'Όνομα',
          name: 'name',
          options: {
            attrs: {
              type: 'text',
              name: 'name',
              class: 'w-full',
            },
          },
          validation: {
            required: true,
            min: 5,
          },
        },
        {
          component: 'BaseFormInput',
          label: 'Έναρξη',
          name: 'dateStart',
          options: {
            attrs: {
              type: 'date',
              name: 'dateStart',
            },
          },
          validation: {
            required: true,
          },
        },
        {
          component: 'BaseFormInput',
          label: 'Λήξη',
          name: 'dateEnd',
          options: {
            attrs: {
              type: 'date',
              name: 'dateEnd',
            },
          },
          validation: {
            //
          },
        },
        {
          component: 'BaseFormSelect',
          label: 'Στόχος',
          name: 'target',
          options: {
            attrs: {
              name: 'target',
            },
            props: {
              options: [
                { value: 'leads', label: 'Leads' },
                { value: 'views', label: 'Views' },
                { value: 'likes', label: 'Likes' },
              ],
            },
          },
          validation: {
            required: true,
            oneOf: ['leads', 'views', 'likes'],
          },
        },
        {
          component: 'BaseFormTextarea',
          label: 'Περιγραφή',
          name: 'description',
          options: {
            attrs: {
              name: 'description',
            },
          },
          validation: {
            max: 500,
          },
        },
      ];
    },
    formFieldsWithDefaults() {
      return this.formFields.map((x) => ({ ...defaultField, ...x }));
    },
  },
  watch: {
    'data.result': {
      handler(value) {
        const { price, pricePerResult } = this.data;
        if (value && price) {
          this.data.pricePerResult = price / value;
        } else if (value && pricePerResult) {
          this.data.price = value * pricePerResult;
        }
      },
    },
    'data.pricePerResult': {
      handler(value) {
        const { result, price } = this.data;
        if (value && result) {
          this.data.price = value * result;
        } else if (value && price) {
          this.data.result = price / value;
        }
      },
    },
    'data.price': {
      handler(value) {
        const { result, pricePerResult } = this.data;
        if (value && pricePerResult) {
          this.data.result = value / pricePerResult;
        } else if (value && result) {
          this.data.pricePerResult = value / result;
        }
      },
    },
  },
  created() {
    this.data = { ...defaultData, ...this.campaign };
  },
  methods: {
    async doSubmit() {
      const submitWithErrorHandling = this.handleFormErrors(this.action);

      this.submitting = true;

      await submitWithErrorHandling(this.data);

      this.submitting = false;
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            if (this.closeOnSuccess) {
              this.$refs.modal.doClose();
            }
          })
          .catch(({ status, data } = {}) => {
            // do sth with the error!
            if (status && status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            }
          });
    },
  },
};
</script>
